import React, { Fragment, useEffect, useState } from "react";
import * as Yup from "yup";
import {
  Button,
  Dialog as MUIDialog,
  Grid,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import { Cancel } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useSelector } from "react-redux";

import { useAxios, useComponent } from "../../../../hooks";
import { Autocomplete, TextField } from "../../../../components";
import FilterDataForm from "../../../leads/lead list components/FilterDataForm";

const useStyles = makeStyles(() => ({
  paper: { minWidth: "1000px" },
}));

export default function FilterDataDialog({
  formikProps,
  setNewSegment,
  setFilter,
  filter,
  disable,
}) {
  const axios = useAxios();
  const classes = useStyles();

  const [labels, setLabels] = useState([]);
  const [column, setColumn] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memberList, setMemberList] = useState([]);
  const [applyFilter, setApplyFilter] = useState({
    isFilter: false,
    data: "",
    url: "",
  });

  const { bots, activeBot } = useSelector((state) => state.memberDetails);

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick") return;

    setFilter(false);
  };

  useEffect(() => {
    if (filter) {
      setLoading(true);
      axios({
        url: `/label/${activeBot.ID}`,
      }).then((response) => {
        if (response.status) {
          setLabels(response.data);
        }
      });

      axios({
        url: "/leads/members_list",
        disableRedirect: true,
      }).then((response) => {
        if (response.status) {
          setMemberList(response.data);
        }
      });

      axios({
        url:
          "/custom_fields/get_fields_for_agent?agent_id=" +
          activeBot.ID +
          "&is_active=1",
        method: "GET",
      }).then((response) => {
        if (response.status) {
          let filterColumns = [
            {
              name: "name",
              label: "Name",
              dataType: "string",
            },
            {
              name: "email",
              label: "Email",
              dataType: "string",
            },
            {
              name: "phone",
              label: "Phone",
              dataType: "string",
            },
            {
              name: "created_by",
              label: "Created By",
              dataType: "string",
            },
          ];
          for (let field of response.data) {
            filterColumns.push({
              name: field.name,
              label: field.label,
              dataType: field.value_type,
              ...(field.control_id === "61dd2dfa1fb38be82e077ed8" && {
                options: field.options,
              }),
            });
          }
          setColumn(filterColumns);
        }
      });
      setLoading(false);
    }
  }, [axios, filter, activeBot]);

  const [filterData, setFilterData] = useState({
    segmentName: formikProps.values.segmentName,
    filters: formikProps.values.segmentFilter || [
      {
        key: "",
        value: "",
        operator: "",
        relation: "",
      },
    ],
    filtersLabels: formikProps.values.segmentLabels || [],
  });

  return (
    <React.Fragment>
      <MUIDialog
        classes={{ paper: classes.paper }}
        open={filter}
        onClose={handleClose}
      >
        <Grid
          container
          spacing={0}
          style={{
            borderBottom: "1px solid #cbcbcb",
          }}
        >
          <Grid style={{ padding: "16px 24px" }} item xs={8}>
            <Typography variant="h5">Filter Data</Typography>
          </Grid>

          <Grid item xs={4}>
            <IconButton
              disableRipple
              style={{ float: "right", padding: "16px 24px" }}
              onClick={handleClose}
            >
              <Cancel />
            </IconButton>
          </Grid>
        </Grid>

        <Formik
          initialValues={filterData}
          enableReinitialize
          validateOnMount
          validationSchema={validationSchema}
          onSubmit={(formData, { setSubmitting }) => {
            let url =
              "/reminders/segments/" + activeBot.ID + "?skip=0&limit=50";

            let filterData = {
              segment_name: formData.segmentName,
            };
            if (
              formData.filters[0].operator !== undefined &&
              formData.filters[0].operator !== ""
            ) {
              filterData.filters = [];
              for (let data of formData.filters) {
                filterData.filters.push({
                  key: data.key,
                  value: data.value,
                  operator: data.operator,
                  relation: data.relation,
                });
              }
              url = url + "&filter=1";
              setApplyFilter((pre) => ({
                isFilter: true,
                url: "&filter=1",
                data: pre.filterData,
              }));
            }
            filterData.labels = [];

            for (let data of formData.filtersLabels) {
              filterData.labels.push(data._id);
            }
            setFilterData(formData);
            axios({
              url: url,
              method: "POST",
              data: filterData,
            }).then((response) => {
              if (response.status) {
                setApplyFilter((pre) => ({
                  isFilter: pre.isFilter,
                  url: pre.url,
                  data: filterData,
                }));
                setNewSegment(response.data?.segment_id);

                handleClose();
              }
              setSubmitting(false);
            });
          }}
        >
          {(dialogFormikProps) => (
            <React.Fragment>
              <DialogContent
                style={{
                  paddingTop: "10px",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption1"
                      style={{ color: "rgb(128, 128, 128)" }}
                    >
                      Apply filter to the table data
                    </Typography>
                  </Grid>

                  {loading ? (
                    <Grid item xs={12} style={{ textAlign: "center" }}>
                      <CircularProgress />
                    </Grid>
                  ) : (
                    <Fragment>
                      <Grid item xs={4}>
                        <TextField
                          name="segmentName"
                          label={"Segment Name"}
                          formikProps={dialogFormikProps}
                          disabled={disable}
                        />
                      </Grid>
                      <Grid item xs={8} />

                      <FilterDataForm
                        bots={bots}
                        disable={disable}
                        memberList={memberList}
                        dialogFormikProps={dialogFormikProps}
                        filterColumns={column}
                      />

                      <Divider />

                      <Grid item container xs={12}>
                        <Typography
                          variant="caption1"
                          style={{ color: "rgb(128, 128, 128)" }}
                        >
                          Apply filter to the Label
                        </Typography>
                      </Grid>

                      <Grid item xs={8}>
                        <Autocomplete
                          multiple={true}
                          name="filtersLabels"
                          label="Select Fields"
                          defaults={{
                            primaryKey: "title",
                            displayLabel: "title",
                          }}
                          required={false}
                          options={labels}
                          formikProps={dialogFormikProps}
                          sx={{ mt: "10px" }}
                          disableField={disable}
                        />
                      </Grid>
                    </Fragment>
                  )}
                </Grid>
              </DialogContent>

              {!disable && (
                <DialogActions style={{ padding: "12px 25px 24px" }}>
                  <Grid container spacing={3}>
                    <Grid item xs={9}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleClose}
                      >
                        Cancel
                      </Button>
                    </Grid>

                    <Grid
                      item
                      xs={3}
                      display={"flex"}
                      justifyContent={"space-between"}
                      style={{ textAlign: "end" }}
                    >
                      <Button
                        onClick={handleClearFilter}
                        variant="contained"
                        color="primary"
                        disabled={dialogFormikProps.dirty}
                      >
                        Clear Filter
                      </Button>

                      <Button
                        onClick={dialogFormikProps.handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!dialogFormikProps.dirty}
                      >
                        Apply Filter
                      </Button>
                    </Grid>
                  </Grid>
                </DialogActions>
              )}
            </React.Fragment>
          )}
        </Formik>
      </MUIDialog>
    </React.Fragment>
  );

  function handleClearFilter() {
    setFilterData({
      filters: [
        {
          key: "",
          value: "",
          operator: "",
          relation: "",
        },
      ],
      filtersLabels: [],
    });
  }
}
const validationSchema = Yup.object().shape({
  filters: Yup.array(
    Yup.object().shape({
      key: Yup.string(),
      operator: Yup.string(),
      value: Yup.string(),
    })
  ),
});
